import { fr } from "date-fns/locale/fr";
import { t } from "i18next";
import { Calendar as CalendarIcon } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { Control, FieldPath, FieldValues, useController } from "react-hook-form";

import { MaskedInput } from "src/core/components/custom-ui/masked-input";
import { Button } from "src/core/components/ui/button";
import { Calendar } from "src/core/components/ui/calendar";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/core/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "src/core/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/core/components/ui/select";
import { cn } from "src/core/lib/utils";

const months = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

const currentYear = new Date().getFullYear();

type DatePickerProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>;
  required?: boolean;
  softRequired?: boolean;
  className?: string;
  label?: string;
  value?: string;
  onChange?: (date: string) => void;
  control?: Control<T>;
  allowFutureDates?: boolean;
  allowPastDates?: boolean;
  maxDate?: Date;
  minDate?: Date;
  disabled?: boolean;
};

/**
 * @param softRequired - (boolean) Permet de gérer un affichage de type "warning" (orange)
 * pour les champs qui sont nécessaires à la validation d'un dossier.
 */
export function DatePicker<T extends FieldValues = FieldValues>({
  name,
  required,
  softRequired,
  className,
  label,
  value,
  onChange,
  control,
  allowFutureDates = true,
  allowPastDates = true,
  maxDate,
  minDate,
  disabled = false,
}: DatePickerProps<T>) {
  const {
    field: { value: formValue, onChange: formOnChange },
  } = useController({
    name,
    control,
  });

  // Calcul des dates autorisées
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Génération dynamique des années selon les paramètres
  const getAvailableYears = () => {
    const years: number[] = [];

    if (allowPastDates) {
      // Ajouter les années passées (100 ans en arrière)
      for (let i = 0; i < 100; i++) {
        years.push(currentYear - i);
      }
    }

    if (allowFutureDates) {
      // Ajouter les années futures (50 ans en avant)
      for (let i = 1; i <= 50; i++) {
        years.push(currentYear + i);
      }
    }

    // Ajouter l'année actuelle si elle n'est pas déjà incluse
    if (!years.includes(currentYear)) {
      years.push(currentYear);
    }

    // Trier les années par ordre décroissant
    return years.sort((a, b) => b - a);
  };

  const years = getAvailableYears();

  const getFromDate = () => {
    if (minDate) return minDate;
    if (!allowPastDates) return today;
    return undefined;
  };

  const getToDate = () => {
    if (maxDate) return maxDate;
    if (!allowFutureDates) return today;
    return undefined;
  };

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    value ? new Date(value) : formValue ? new Date(formValue) : undefined
  );
  const [selectedMonth, setSelectedMonth] = useState<number>(
    value
      ? new Date(value).getMonth()
      : formValue
      ? new Date(formValue).getMonth()
      : new Date().getMonth()
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    value
      ? new Date(value).getFullYear()
      : formValue
      ? new Date(formValue).getFullYear()
      : currentYear
  );

  const handleMonthChangeFromCalendar = (newMonth: Date) => {
    setSelectedMonth(newMonth.getMonth());
    setSelectedYear(newMonth.getFullYear());
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      setSelectedMonth(date.getMonth());
      setSelectedYear(date.getFullYear());

      const luxonDate = DateTime.fromJSDate(date).setZone("Europe/Paris").startOf("day");
      const formattedDate = luxonDate.toISO() || "";

      if (onChange) {
        onChange(formattedDate);
      }
      if (formOnChange) {
        formOnChange(formattedDate);
      }
    }
  };

  const handleMonthChange = (month: number) => {
    setSelectedMonth(month);
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      newDate.setMonth(month);
      handleDateSelect(newDate);
    }
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      newDate.setFullYear(year);
      handleDateSelect(newDate);
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return "";
    return DateTime.fromJSDate(date).setZone("Europe/Paris").toFormat("dd/MM/yyyy");
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const isInvalid = fieldState.invalid;
        const showSoftRequired = softRequired && !isInvalid;

        return (
          <FormItem>
            <FormLabel required={required} className={cn(isInvalid && "text-alert")}>
              {label}
            </FormLabel>
            <FormControl>
              <div
                className={cn(
                  "flex items-center relative",
                  disabled && "opacity-50 cursor-not-allowed"
                )}
              >
                <MaskedInput
                  mask="##/##/####"
                  placeholder="JJ/MM/AAAA"
                  className={cn(
                    "form-input",
                    className,
                    isInvalid && "border-alert",
                    showSoftRequired && "border-2 border-dashed border-orange-medium"
                  )}
                  name={name}
                  value={selectedDate ? formatDate(selectedDate) : ""}
                  disabled={disabled}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (!value || value.trim() === "") {
                      setSelectedDate(undefined);
                      field.onChange("");
                      if (onChange) onChange("");
                      return;
                    }

                    if (value.length === 10 && value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                      const [day, month, year] = value.split("/").map(Number);
                      if (day && month && year) {
                        const date = new Date(year, month - 1, day);
                        if (!isNaN(date.getTime())) {
                          setSelectedDate(date);
                          field.onChange(
                            DateTime.fromJSDate(date)
                              .setZone("Europe/Paris")
                              .startOf("day")
                              .toISO() || ""
                          );
                          if (onChange)
                            onChange(
                              DateTime.fromJSDate(date)
                                .setZone("Europe/Paris")
                                .startOf("day")
                                .toISO() || ""
                            );
                          return;
                        }
                      }
                    }
                  }}
                />
                <Popover>
                  <PopoverTrigger asChild disabled={disabled}>
                    <Button
                      variant="ghost"
                      className={cn("absolute right-0 h-full px-3", "hover:bg-transparent")}
                      disabled={disabled}
                    >
                      <CalendarIcon className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="flex gap-2 p-3">
                      <Select
                        value={selectedMonth.toString()}
                        onValueChange={(value) => handleMonthChange(parseInt(value))}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Mois" />
                        </SelectTrigger>
                        <SelectContent>
                          {months.map((month, index) => (
                            <SelectItem key={month} value={index.toString()}>
                              {month}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={selectedYear.toString()}
                        onValueChange={(value) => handleYearChange(parseInt(value))}
                      >
                        <SelectTrigger className="w-[100px]">
                          <SelectValue placeholder="Année" />
                        </SelectTrigger>
                        <SelectContent>
                          {years.map((year) => (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="p-3">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => handleDateSelect(date)}
                        month={new Date(selectedYear, selectedMonth)}
                        onMonthChange={handleMonthChangeFromCalendar}
                        locale={fr}
                        showOutsideDays
                        fixedWeeks
                        fromDate={getFromDate()}
                        toDate={getToDate()}
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </FormControl>
            {fieldState.error && <FormMessage>{fieldState.error.message}</FormMessage>}
            {showSoftRequired && (
              <p className="font-light text-sm text-muted">
                {t("global.form.warning.softRequired")}
              </p>
            )}
          </FormItem>
        );
      }}
    />
  );
}
